import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

// @material-ui/core components
import { withStyles } from "@mui/styles";
import {
  Card,
  CardBody,
  Button,
  GridContainer,
  GridItem,
} from "~/shared/components/MuiComponents.jsx";

import consultationStyles from "../styles/consultationStyles.js";
import {
  FlavorPhoto,
  PRODUCT_PHOTO_SCALE_TABLETS,
} from "~/visits/utils/visitComponents.jsx";

import imgChocoChillMint from "~assets/images/flavors/choco-chill.png";
import imgCitrusZen from "~assets/images/flavors/citrus-zen.png";
import imgChocoChillMintLegacy from "~assets/images/flavors/legacy/choco-chill.png";
import imgCitrusZenLegacy from "~assets/images/flavors/legacy/citrus-zen.png";

import {
  getOrderItemSubcategory,
  getVisitID,
  getVisitProduct,
  getVisitUSState,
  isFollowUpVisit,
  setConsultationTypeOrderItem,
  setOrderItemSubcategory,
  setVisitProduct,
} from "~/visits/utils/visitCookies";

import {
  CHOCO_CHILL_MINT_FLAVOR,
  CITRUS_ZEN_FLAVOR,
  COLOR_KICK_BLUE,
  ORDER_TYPE_ATE_SCO_1_PACK,
  ORDER_TYPE_ATE_SCO_1_PACK_FOLLOW_UP,
  ORDER_TYPE_ATE_SCO_1_PACK_REFILL,
  ORDER_TYPE_ATE_SCO_2_PACK,
  ORDER_TYPE_ATE_SCO_2_PACK_FOLLOW_UP,
  ORDER_TYPE_ATE_SCO_2_PACK_REFILL,
} from "~/shared/sharedConstants.js";

import { supportedUSStatesForCompoundedMeds } from "~/shared/utils/sharedUtils";

import { kickBlue } from "~/shared/styles/baseStyles";
import { useFlavorPhotosExperiment } from "~/shared/experiments/product/flavorPhotos";

const flavors = [
  {
    name: "Choco Chill Mint",
    image: imgChocoChillMint,
    legacyImage: imgChocoChillMintLegacy,
    imageScale: PRODUCT_PHOTO_SCALE_TABLETS,
    flavorConst: CHOCO_CHILL_MINT_FLAVOR,
    longDescription:
      "Smooth chocolate meets refreshing mint in a fast-acting lozenge. Indulgent calmness when you need it most.",
  },
  {
    name: "Citrus Zen",
    image: imgCitrusZen,
    legacyImage: imgCitrusZenLegacy,
    imageScale: PRODUCT_PHOTO_SCALE_TABLETS,
    longDescription:
      "Bright citrus notes with a hint of lemon in a fast-acting lozenge. Find your zen with this refreshing flavor.",
    flavorConst: CITRUS_ZEN_FLAVOR,
  },
];

const SelectGAFlavored = ({ classes, history, match }) => {
  const consultationType = match.params.consultationType;
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const checkoutSessionID = queryParams.get("checkoutSessionID");
  if (!checkoutSessionID) {
    window.location.href = "/me/portal";
  }

  // Experiment product-flavor-photos: which photo set (and row padding) this
  // checkout renders. Rows wait for `photosReady` so the patient never sees one
  // set swapped for the other.
  const { ready: photosReady, reframed } = useFlavorPhotosExperiment({
    checkoutSessionID,
    consultationType,
  });
  const productRowClass = reframed
    ? classes.selectFlavoredProductWrapperReframed
    : classes.selectFlavoredProductWrapper;

  const [selectedFlavor, setSelectedFlavor] = useState(
    getOrderItemSubcategory(consultationType, checkoutSessionID) || null,
  );
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    if (!getVisitID(checkoutSessionID)) {
      window.location.href = "/me/portal";
      return;
    }

    const visitIDCheckPoller = setInterval(() => {
      if (!getVisitID(checkoutSessionID)) {
        window.location.href = "/me/portal";
      }
    }, 1000);

    if (!supportedUSStatesForCompoundedMeds.includes(getVisitUSState())) {
      history.replace(
        `/visits/${consultationType}/payment/order-details?checkoutSessionID=${checkoutSessionID}`,
      );
    }

    return () => clearInterval(visitIDCheckPoller);
  }, []);

  const handleSelection = (selectedFlavor) => {
    setSelectedFlavor(selectedFlavor);
    setShowError(false);
  };

  const setVisitProductAndOrderItem = (productSlug) => {
    setVisitProduct(productSlug, checkoutSessionID);
    setConsultationTypeOrderItem(
      consultationType,
      productSlug,
      checkoutSessionID,
    );
  };

  const nextQuestion = () => {
    if (selectedFlavor === null) {
      setShowError(true);
      return;
    }

    const currentProduct = getVisitProduct(checkoutSessionID) || "";
    const isTwoPack = currentProduct.includes("2-pack");

    // Set the appropriate GA product based on pack size and visit type
    if (isFollowUpVisit(checkoutSessionID)) {
      // For follow-up visits, use refill pricing
      const isRefill = currentProduct.includes("refill");
      if (isRefill) {
        setVisitProductAndOrderItem(
          isTwoPack
            ? ORDER_TYPE_ATE_SCO_2_PACK_REFILL
            : ORDER_TYPE_ATE_SCO_1_PACK_REFILL,
        );
      } else {
        setVisitProductAndOrderItem(
          isTwoPack
            ? ORDER_TYPE_ATE_SCO_2_PACK_FOLLOW_UP
            : ORDER_TYPE_ATE_SCO_1_PACK_FOLLOW_UP,
        );
      }
    } else {
      setVisitProductAndOrderItem(
        isTwoPack ? ORDER_TYPE_ATE_SCO_2_PACK : ORDER_TYPE_ATE_SCO_1_PACK,
      );
    }

    // Set the selected flavor
    setOrderItemSubcategory(
      consultationType,
      selectedFlavor,
      checkoutSessionID,
    );

    // Navigate to payment
    history.push(
      `/visits/${consultationType}/payment/order-details?checkoutSessionID=${checkoutSessionID}`,
    );
    window.scrollTo(0, 0);
  };

  const isSelected = (flavorConst) => {
    return selectedFlavor === flavorConst;
  };

  return (
    <div
      className={classes.container}
      style={{ backgroundColor: COLOR_KICK_BLUE }}
    >
      <GridContainer justifyContent="center">
        <GridItem xs={12} sm={10} md={6}>
          <h3
            style={{
              color: "#FFFFFF",
              fontFamily: "Spectral-SemiBold",
              fontSize: "32px",
              marginTop: "40px",
            }}
          >
            Choose Your Flavor
          </h3>
          <p
            style={{
              color: "#FFFFFF",
              fontFamily: "BasisGrotesquePro-Regular",
              fontSize: "18px",
              lineHeight: "26px",
            }}
          >
            Our specially formulated Atenolol + Scopolamine combination comes in
            two delicious flavors. These fast-acting lozenges dissolve quickly
            for rapid relief when you need it most.
            <br />
            <br />
          </p>
        </GridItem>
        <GridItem xs={12} sm={12} md={12}></GridItem>
        <GridItem xs={12} sm={10} md={6}>
          <Card
            className={classes.cardConsultation}
            style={{ paddingTop: "20px", paddingBottom: "20px" }}
          >
            <CardBody
              style={{
                display: "flex",
                flexDirection: "column",
                padding: "0 2px",
              }}
            >
              {!photosReady ? (
                <div
                  role="status"
                  aria-busy="true"
                  aria-label="Loading product options"
                  style={{ minHeight: "160px" }}
                />
              ) : (
                <>
                  {flavors.map((flavor, idx) => {
                    return (
                      <div key={flavor.flavorConst}>
                        <div
                          className={productRowClass}
                          style={{
                            overflow: "hidden",
                            backgroundColor: isSelected(flavor.flavorConst)
                              ? "#fff7e5"
                              : "",
                          }}
                          onClick={() => handleSelection(flavor.flavorConst)}
                        >
                          <FlavorPhoto
                            reframed={reframed}
                            src={flavor.image}
                            legacySrc={flavor.legacyImage}
                            alt={`${flavor.name} Flavor`}
                            scale={flavor.imageScale}
                          />
                          <div
                            className={classes.selectFlavorImageTitleWrapper}
                          >
                            <h4
                              className={classes.selectFlavorImageTitle}
                              style={{
                                color: kickBlue,
                                fontWeight: "bold",
                              }}
                            >
                              {flavor.name}
                            </h4>
                            <p
                              style={{
                                marginTop: "4px",
                                marginBottom: "8px",
                                fontSize: "16px",
                              }}
                            >
                              {flavor.longDescription}
                            </p>
                          </div>
                        </div>
                        {idx !== flavors.length - 1 && (
                          <div
                            style={{
                              height: "1px",
                              margin: "0 25%",
                              backgroundColor: "#aaa",
                            }}
                          />
                        )}
                      </div>
                    );
                  })}
                </>
              )}
            </CardBody>
          </Card>
          <div style={{ color: "red", margin: "20px auto 6px auto" }}>
            <p style={{ textAlign: "center" }}>
              {showError ? "Please select a flavor to continue" : ""}
            </p>
          </div>
          <Button
            onClick={() => {
              nextQuestion();
            }}
            style={{
              display: "block",
              backgroundColor: "#F25930",
              margin: "16px auto",
              width: "300px",
              fontSize: "18px",
              fontWeight: "medium",
              textTransform: "unset",
            }}
          >
            Continue
          </Button>
        </GridItem>
      </GridContainer>
    </div>
  );
};

export default withStyles(consultationStyles)(SelectGAFlavored);
